import { usePermissions } from './usePermissions';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { useUsage } from './useUsage';

/**
 * Custom hook for fetching domains with caching
 * Cached for 2 minutes since domains change less frequently
 */
export function useDomains(enabled: boolean = true) {
  const { workspaceId, hasPermission } = usePermissions();
  return useQuery({
    queryKey: ['domains', workspaceId],
    queryFn: async () => {
      const response = await apiService.getDomains(workspaceId);
      return response.domains || [];
    },
    staleTime: 120000, // 2 minutes
    gcTime: 300000, // 5 minutes
    enabled: enabled && !!workspaceId && hasPermission('domain:read'),
  });
}

/**
 * Custom hook for fetching API keys with caching
 * Cached for 2 minutes
 */
export function useApiKeys({
  transactional = false,
  marketing = false,
  webSetup = false,
}: {
  transactional?: boolean;
  marketing?: boolean;
  webSetup?: boolean;
} = {}) {
  const { workspaceId, hasPermission } = usePermissions();
  transactional = transactional && hasPermission('transactional_api_key:read');
  marketing = marketing && hasPermission('marketing_api_key:read');
  webSetup = webSetup && hasPermission('web_api_key:read');
  const enabled = !!workspaceId && (transactional || marketing || webSetup);
  return useQuery({
    queryKey: ['apiKeys', workspaceId, { transactional, marketing, webSetup }, 'unified'],
    queryFn: async () => {
      const types = [
        ...(transactional ? ['transactional'] : []),
        ...(marketing ? ['marketing'] : []),
        ...(webSetup ? ['web-setup'] : []),
      ];
      const response = await apiService.getAllApiKeys(workspaceId, types);
      return response.apiKeys || [];
    },
    staleTime: 120000, // 2 minutes
    gcTime: 300000, // 5 minutes
    enabled,
  });
}

/**
 * Custom hook for fetching SMTP credentials with caching
 * Cached for 2 minutes
 */
export function useSMTPCredentials(enabled: boolean = true) {
  const { workspaceId, hasPermission } = usePermissions();
  return useQuery({
    queryKey: ['smtp', workspaceId],
    queryFn: async () => {
      const response = await apiService.getSMTPCredentials(workspaceId);
      return response.credentials || [];
    },
    staleTime: 120000, // 2 minutes
    gcTime: 300000, // 5 minutes
    enabled: enabled && !!workspaceId && hasPermission('smtp:read'),
  });
}

/**
 * Combined hook for all HomePage data
 * Fetches all data in parallel with individual caching
 */
export function useHomePageData({
  canReadDomains = false,
  canReadTransactionalApiKeys = false,
  canReadMarketingApiKeys = false,
  canReadWebSetupApiKeys = false,
  canReadSmtp = false,
  canReadBilling = false,
}: {
  canReadDomains?: boolean;
  canReadTransactionalApiKeys?: boolean;
  canReadMarketingApiKeys?: boolean;
  canReadWebSetupApiKeys?: boolean;
  canReadSmtp?: boolean;
  canReadBilling?: boolean;
} = {}) {
  const usage = useUsage(canReadBilling);
  const domains = useDomains(canReadDomains);
  const apiKeys = useApiKeys({
    transactional: canReadTransactionalApiKeys,
    marketing: canReadMarketingApiKeys,
    webSetup: canReadWebSetupApiKeys,
  });
  const smtp = useSMTPCredentials(canReadSmtp);

  return {
    usage: usage.data,
    domains: domains.data || [],
    apiKeys: apiKeys.data || [],
    smtp: smtp.data || [],
    isLoading: usage.isLoading || domains.isLoading || apiKeys.isLoading || smtp.isLoading,
    isError: usage.isError || domains.isError || apiKeys.isError || smtp.isError,
    error: usage.error || domains.error || apiKeys.error || smtp.error,
    refetch: () => {
      if (canReadBilling) usage.refetch();
      if (canReadDomains) domains.refetch();
      if (canReadTransactionalApiKeys || canReadMarketingApiKeys || canReadWebSetupApiKeys) apiKeys.refetch();
      if (canReadSmtp) smtp.refetch();
    },
  };
}
